import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { UpdateUserProfileData, UserProfileDialogProps } from '../types';

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({
  user,
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<UpdateUserProfileData>({
    name: '',
    dni: '',
    telefono: '',
    direccion: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !user) return;

    setFormData({
      name: user.name ?? '',
      dni: user.profile?.dni ?? '',
      telefono: user.profile?.telefono ?? '',
      direccion: user.profile?.direccion ?? '',
    });
    setErrors({});
  }, [open, user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!(formData.name ?? '').trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if ((formData.name ?? '').trim().length > 255) {
      newErrors.name = 'El nombre no puede superar 255 caracteres';
    }

    if ((formData.dni ?? '').length > 50) {
      newErrors.dni = 'El DNI no puede superar 50 caracteres';
    }

    if ((formData.telefono ?? '').length > 50) {
      newErrors.telefono = 'El teléfono no puede superar 50 caracteres';
    }

    if ((formData.direccion ?? '').length > 255) {
      newErrors.direccion = 'La dirección no puede superar 255 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeNullable = (value?: string | null): string | null => {
    if (value === undefined || value === null) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  const handleSave = async () => {
    if (!user || !validateForm()) return;

    setSaving(true);
    try {
      await onSave(user.id, {
        name: (formData.name ?? '').trim(),
        dni: normalizeNullable(formData.dni),
        telefono: normalizeNullable(formData.telefono),
        direccion: normalizeNullable(formData.direccion),
      });
      onClose();
    } catch (error) {
      // Error handling is done in parent hook.
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setErrors({});
      onClose();
    }
  };

  const handleInputChange = (field: keyof UpdateUserProfileData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Datos Personales</DialogTitle>
          <DialogDescription>
            Actualizá los datos de <strong>{user.name}</strong>
            {user.email ? ` (${user.email})` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={formData.name ?? ''}
              onChange={handleInputChange('name')}
              placeholder="Nombre completo"
              disabled={saving}
              maxLength={255}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dni">DNI</Label>
            <Input
              id="dni"
              value={formData.dni ?? ''}
              onChange={handleInputChange('dni')}
              placeholder="Ej: 30123456"
              disabled={saving}
              maxLength={50}
              className={errors.dni ? 'border-red-500' : ''}
            />
            {errors.dni && <p className="text-sm text-red-500">{errors.dni}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefono">Teléfono</Label>
            <Input
              id="telefono"
              value={formData.telefono ?? ''}
              onChange={handleInputChange('telefono')}
              placeholder="Ej: +54 9 261 1234567"
              disabled={saving}
              maxLength={50}
              className={errors.telefono ? 'border-red-500' : ''}
            />
            {errors.telefono && <p className="text-sm text-red-500">{errors.telefono}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="direccion">Dirección</Label>
            <Textarea
              id="direccion"
              value={formData.direccion ?? ''}
              onChange={handleInputChange('direccion')}
              placeholder="Ingrese la dirección"
              disabled={saving}
              maxLength={255}
              className={errors.direccion ? 'border-red-500' : ''}
            />
            {errors.direccion && <p className="text-sm text-red-500">{errors.direccion}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} loading={saving}>
            Guardar Datos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileDialog;
